import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Plus, MagnifyingGlass, ArrowLeft, Upload, Download, Trash, Check, X,
  Wallet, Student, CalendarBlank, Scales, Users, FileText, Clock, TrendUp,
  PencilSimple, Warning, ArrowsDownUp
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const PIE_COLORS = ['#0F6CBD', '#107C41', '#D83B01', '#FFB900', '#D13438', '#5C2D91', '#0078D4', '#00B294'];
const fmtCurrency = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function RecipientsHub() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editRecipient, setEditRecipient] = useState(null);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const fetchRecipients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterType && filterType !== 'all') params.set('type', filterType);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      const { data } = await api.get(`/recipients?${params}`);
      setRecipients(data);
    } catch { toast.error('Failed to load recipients'); }
    setLoading(false);
  }, [search, filterType, filterStatus]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);
  useEffect(() => {
    api.get('/admin/schools').then(r => setSchools(r.data)).catch(() => {});
    api.get('/admin/categories?type=recipient').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const selectRecipient = async (id) => {
    try {
      const [recip, st] = await Promise.all([
        api.get(`/recipients/${id}`),
        api.get(`/recipients/${id}/stats`)
      ]);
      setSelected(recip.data);
      setStats(st.data);
    } catch { toast.error('Failed to load recipient'); }
  };

  // RECIPIENT FORM
  const RecipientForm = () => {
    const [form, setForm] = useState(editRecipient || {
      name: '', type: 'student', email: '', phone: '', age: '', gender: '', date_of_birth: '',
      grade: '', school_id: '', category: '', financial_requirement: '', address: '', notes: ''
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm({...form, [k]: v});
    const handleSave = async () => {
      if (!form.name) { toast.error('Name is required'); return; }
      setSaving(true);
      try {
        const payload = { ...form, age: form.age ? parseInt(form.age) : null, financial_requirement: parseFloat(form.financial_requirement) || 0 };
        if (editRecipient?.id) {
          await api.put(`/recipients/${editRecipient.id}`, payload);
          toast.success('Recipient updated');
          selectRecipient(editRecipient.id);
        } else {
          await api.post('/recipients', payload);
          toast.success('Recipient created');
        }
        setShowForm(false); setEditRecipient(null); fetchRecipients();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setSaving(false);
    };
    return (
      <Dialog open onOpenChange={() => { setShowForm(false); setEditRecipient(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recipient-form-dialog">
          <DialogHeader><DialogTitle className="font-['Chivo']">{editRecipient?.id ? 'Edit Recipient' : 'New Recipient'}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Name *</Label><Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="recipient-name-input" className="rounded-sm" /></div>
              <div><Label className="data-label">Type</Label>
                <Select value={form.type} onValueChange={v => set('type', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="recipient-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="student">Student</SelectItem><SelectItem value="school">School</SelectItem><SelectItem value="fund">Fund</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="data-label">Age</Label><Input type="number" value={form.age} onChange={e => set('age', e.target.value)} className="rounded-sm" /></div>
              <div><Label className="data-label">Gender</Label>
                <Select value={form.gender || ''} onValueChange={v => set('gender', v)}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="data-label">Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} className="rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Grade</Label><Input value={form.grade} onChange={e => set('grade', e.target.value)} className="rounded-sm" /></div>
              <div><Label className="data-label">School</Label>
                <Select value={form.school_id || ''} onValueChange={v => set('school_id', v)}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select school" /></SelectTrigger>
                  <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Category</Label>
                <Select value={form.category || ''} onValueChange={v => set('category', v)}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="data-label">Financial Requirement ($)</Label>
                <Input type="number" step="0.01" value={form.financial_requirement} onChange={e => set('financial_requirement', e.target.value)} className="rounded-sm" data-testid="recipient-financial-req" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Email</Label><Input type="email" value={form.email} onChange={e => set('email', e.target.value)} className="rounded-sm" /></div>
              <div><Label className="data-label">Phone</Label><Input value={form.phone} onChange={e => set('phone', e.target.value)} className="rounded-sm" /></div>
            </div>
            <div><Label className="data-label">Address</Label><Textarea value={form.address} onChange={e => set('address', e.target.value)} className="rounded-sm" rows={2} /></div>
            <div><Label className="data-label">Notes</Label><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="rounded-sm" rows={2} /></div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm" data-testid="recipient-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  /* ═══════════════ DETAIL VIEW ═══════════════ */
  if (selected) {
    const s = stats || {};
    return (
      <div className="space-y-5" data-testid="recipient-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setStats(null); }} data-testid="back-to-recipients-btn"><ArrowLeft size={18} /></Button>
            <div className="w-10 h-10 bg-[#107C41] rounded-sm flex items-center justify-center text-white font-bold text-lg font-['Chivo']">
              {selected.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 font-['Chivo'] flex items-center gap-2">
                {selected.name}
                <Badge className={`capitalize text-xs ${selected.status === 'active' ? 'status-active' : selected.status === 'dropout' ? 'status-dropout' : 'status-pending'}`}>{selected.status}</Badge>
              </h2>
              <span className="text-sm text-slate-500">{selected.school_name}{selected.grade ? ` · Grade ${selected.grade}` : ''}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { setEditRecipient(selected); setShowForm(true); }} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm text-xs" data-testid="edit-recipient-btn">
              <PencilSimple size={14} className="mr-1" /> Edit
            </Button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total Allocated', val: fmtCurrency(selected.total_allocated), icon: TrendUp, color: '#0F6CBD' },
            { label: 'Total Spent', val: fmtCurrency(selected.total_spent), icon: Scales, color: '#D83B01' },
            { label: 'Balance', val: fmtCurrency(selected.wallet_balance), icon: Wallet, color: '#107C41' },
            { label: 'Requirement', val: fmtCurrency(selected.financial_requirement), icon: Student, color: '#5C2D91' },
            { label: 'Funding Requests', val: `${s.funding_requests?.approved || 0}/${s.funding_requests?.total || 0}`, icon: FileText, color: '#0078D4' },
            { label: 'Donors', val: s.donor_contributions?.length || 0, icon: Users, color: '#FFB900' },
          ].map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-sm p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold tracking-[0.06em] uppercase text-slate-400">{kpi.label}</span>
                  <Icon size={14} color={kpi.color} weight="duotone" />
                </div>
                <p className="text-base font-semibold text-slate-900 font-mono">{kpi.val}</p>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="flex border-b border-slate-200 bg-transparent p-0 h-auto" data-testid="recipient-tabs">
            {['Overview', 'Financial Balance', 'Ledger', 'Funding Requests', 'Donors', 'Documents'].map(tab => (
              <TabsTrigger key={tab} value={tab.toLowerCase().replace(/\s+/g, '-')}
                className="px-4 py-2.5 text-sm font-medium text-slate-500 border-b-2 border-transparent rounded-none data-[state=active]:text-[#0F6CBD] data-[state=active]:border-[#0F6CBD] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                data-testid={`tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}>{tab}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-4"><OverviewTab recipient={selected} stats={s} onEdit={() => { setEditRecipient(selected); setShowForm(true); }} /></TabsContent>
          <TabsContent value="financial-balance" className="mt-4"><FinancialTab recipientId={selected.id} recipient={selected} stats={s} onRefresh={() => selectRecipient(selected.id)} /></TabsContent>
          <TabsContent value="ledger" className="mt-4"><LedgerTab recipientId={selected.id} recipientName={selected.name} /></TabsContent>
          <TabsContent value="funding-requests" className="mt-4"><FundingRequestsTab recipientId={selected.id} recipientName={selected.name} onRefresh={() => selectRecipient(selected.id)} /></TabsContent>
          <TabsContent value="donors" className="mt-4"><DonorsTab stats={s} /></TabsContent>
          <TabsContent value="documents" className="mt-4"><DocumentsTab recipientId={selected.id} /></TabsContent>
        </Tabs>
        {showForm && <RecipientForm />}
      </div>
    );
  }

  /* ═══════════════ LIST VIEW ═══════════════ */
  return (
    <div className="space-y-4" data-testid="recipients-list">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 font-['Chivo']">Recipients</h2>
        <Button onClick={() => { setEditRecipient(null); setShowForm(true); }} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-sm" data-testid="create-recipient-btn">
          <Plus size={16} className="mr-1" /> New Recipient
        </Button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-64">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm border-slate-300" data-testid="recipient-search-input" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-36 rounded-sm"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="student">Student</SelectItem><SelectItem value="school">School</SelectItem><SelectItem value="fund">Fund</SelectItem></SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 rounded-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="dropout">Dropout</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="flex gap-6 text-sm text-slate-500">
        <span><span className="font-semibold text-slate-700">{recipients.length}</span> recipients</span>
        <span>Active: <span className="font-semibold text-emerald-600">{recipients.filter(r => r.status === 'active').length}</span></span>
        <span>Dropouts: <span className="font-semibold text-slate-600">{recipients.filter(r => r.status === 'dropout').length}</span></span>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            {[
              { key: 'name', label: 'Name' },
              { key: 'status', label: 'Status' },
              { key: 'school_name', label: 'School' },
              { key: 'grade', label: 'Grade' },
              { key: 'total_allocated', label: 'Allocated' },
              { key: 'total_spent', label: 'Spent' },
              { key: 'wallet_balance', label: 'Balance' },
            ].map(col => (
              <th key={col.key}
                className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4 cursor-pointer select-none hover:text-slate-800"
                onClick={() => { setSortField(col.key); setSortDir(sortField === col.key && sortDir === 'asc' ? 'desc' : 'asc'); }}>
                <span className="flex items-center gap-1">{col.label}
                  {sortField === col.key && <ArrowsDownUp size={10} className="text-[#0F6CBD]" />}
                </span>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-400">Loading...</td></tr> :
              recipients.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-400">No recipients found</td></tr> :
              [...recipients].sort((a, b) => {
                const av = a[sortField], bv = b[sortField];
                if (['total_allocated', 'total_spent', 'wallet_balance'].includes(sortField))
                  return sortDir === 'asc' ? (av||0)-(bv||0) : (bv||0)-(av||0);
                return sortDir === 'asc' ? String(av||'').localeCompare(String(bv||'')) : String(bv||'').localeCompare(String(av||''));
              }).map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => selectRecipient(r.id)} data-testid={`recipient-row-${r.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-[#107C41]/10 rounded-sm flex items-center justify-center text-[#107C41] font-semibold text-xs shrink-0">{r.name?.[0]?.toUpperCase()}</div>
                      <span className="text-sm font-medium text-slate-800">{r.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4"><Badge className={`capitalize text-xs ${r.status === 'active' ? 'status-active' : 'status-dropout'}`}>{r.status}</Badge></td>
                  <td className="py-3 px-4 text-sm text-slate-600">{r.school_name || '-'}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{r.grade || '-'}</td>
                  <td className="py-3 px-4 currency text-sm">{fmtCurrency(r.total_allocated)}</td>
                  <td className="py-3 px-4 currency text-sm">{fmtCurrency(r.total_spent)}</td>
                  <td className="py-3 px-4 currency text-sm font-medium text-emerald-700">{fmtCurrency(r.wallet_balance)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {showForm && <RecipientForm />}
    </div>
  );
}

/* ─── TAB COMPONENTS ─── */

function OverviewTab({ recipient, stats, onEdit }) {
  const r = recipient;
  const fields = [
    ['Type', r.type], ['Status', r.status], ['Age', r.age], ['Gender', r.gender],
    ['Date of Birth', r.date_of_birth], ['Grade', r.grade], ['School', r.school_name],
    ['Category', r.category], ['Email', r.email], ['Phone', r.phone],
    ['Address', r.address], ['Financial Requirement', fmtCurrency(r.financial_requirement)],
    ['Created', r.created_at?.substring(0, 10)],
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="overview-tab">
      <div className="lg:col-span-2 space-y-4">
        {/* Cash Flow Chart */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] mb-3">Monthly Cash Flow (12 Months)</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(stats?.monthly_in || []).map((m, i) => ({
                month: m.month, in: m.amount, out: stats?.monthly_out?.[i]?.amount || 0
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={v => `$${v}`} />
                <RTooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                <Bar dataKey="in" name="Credits" fill="#107C41" radius={[2, 2, 0, 0]} />
                <Bar dataKey="out" name="Debits" fill="#D83B01" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Expense Categories */}
        {stats?.expense_categories?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] mb-3">Spending by Category</h3>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.expense_categories.slice(0, 8)} dataKey="total" nameKey="category" cx="50%" cy="50%" innerRadius={35} outerRadius={65}
                    label={({ category, total }) => `${category.substring(0, 15)}: $${total.toLocaleString()}`}>
                    {stats.expense_categories.slice(0, 8).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <RTooltip formatter={v => fmtCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      {/* Profile */}
      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] mb-3">Profile</h3>
          <div className="space-y-2">
            {fields.map(([label, val]) => val ? (
              <div key={label} className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-xs text-slate-400">{label}</span>
                <span className="text-sm text-slate-700 capitalize">{val}</span>
              </div>
            ) : null)}
          </div>
          {r.notes && <div className="mt-3 pt-2 border-t border-slate-100"><span className="text-[10px] uppercase text-slate-400">Notes</span><p className="text-xs text-slate-600 mt-1">{r.notes}</p></div>}
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] mb-2">Quick Facts</h3>
          {[
            ['First Transaction', stats?.first_transaction?.substring(0, 10)],
            ['Last Transaction', stats?.last_transaction?.substring(0, 10)],
            ['Total Transactions', stats?.transaction_count],
            ['Documents', stats?.document_count],
          ].map(([l, v]) => (
            <div key={l} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
              <span className="text-xs text-slate-500">{l}</span><span className="text-sm font-medium text-slate-700">{v || '-'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function LedgerTab({ recipientId, recipientName }) {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    api.get(`/recipients/${recipientId}/transactions`).then(r => { setTxs(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [recipientId]);

  const filtered = txs.filter(tx => {
    const txDate = (tx.created_at || '').substring(0, 10);
    if (dateFrom && txDate < dateFrom) return false;
    if (dateTo && txDate > dateTo) return false;
    if (filterType && filterType !== 'all') {
      const isCredit = tx.to_id === recipientId;
      if (filterType === 'credit' && !isCredit) return false;
      if (filterType === 'debit' && isCredit) return false;
    }
    return true;
  });

  const totalCredits = filtered.filter(t => t.to_id === recipientId).reduce((s, t) => s + (t.amount || 0), 0);
  const totalDebits = filtered.filter(t => t.from_id === recipientId).reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="ledger-tab">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700 font-['Chivo']">Transaction Ledger - {recipientName}</span>
        <span className="text-xs text-slate-500">{filtered.length} transactions</span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <span className="text-[10px] font-semibold tracking-[0.06em] uppercase text-slate-400">Total Credits</span>
          <p className="currency text-lg font-semibold text-emerald-600 mt-1">+${totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <span className="text-[10px] font-semibold tracking-[0.06em] uppercase text-slate-400">Total Debits</span>
          <p className="currency text-lg font-semibold text-red-600 mt-1">-${totalDebits.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <span className="text-[10px] font-semibold tracking-[0.06em] uppercase text-slate-400">Net</span>
          <p className={`currency text-lg font-semibold mt-1 ${totalCredits - totalDebits >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            ${(totalCredits - totalDebits).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-end">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-36 rounded-sm"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="credit">Credits Only</SelectItem>
            <SelectItem value="debit">Debits Only</SelectItem>
          </SelectContent>
        </Select>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="rounded-sm w-40" data-testid="ledger-date-from" />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="rounded-sm w-40" data-testid="ledger-date-to" />
        </div>
        {(dateFrom || dateTo || filterType) && (
          <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => { setDateFrom(''); setDateTo(''); setFilterType(''); }}>Clear filters</Button>
        )}
      </div>

      {/* Ledger table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0">
              <tr className="bg-slate-50 border-b border-slate-200">
                {['Date', 'Type', 'Direction', 'Amount', 'From', 'To', 'Description', 'Reference'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-2.5 px-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="py-6 text-center text-sm text-slate-400">Loading ledger...</td></tr> :
                filtered.length === 0 ? <tr><td colSpan={8} className="py-6 text-center text-sm text-slate-400">No transactions found</td></tr> :
                filtered.map(tx => {
                  const isCredit = tx.to_id === recipientId;
                  return (
                    <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-2.5 px-4 text-xs text-slate-600 whitespace-nowrap">{(tx.created_at || '').substring(0, 10)}</td>
                      <td className="py-2.5 px-4"><Badge variant="outline" className="text-[10px] capitalize">{tx.type?.replace(/_/g, ' ')}</Badge></td>
                      <td className="py-2.5 px-4">
                        <Badge className={`text-[10px] ${isCredit ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'} border`}>
                          {isCredit ? 'CREDIT' : 'DEBIT'}
                        </Badge>
                      </td>
                      <td className={`py-2.5 px-4 currency text-sm font-medium ${isCredit ? 'text-emerald-600' : 'text-red-600'}`}>
                        {isCredit ? '+' : '-'}${(tx.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2.5 px-4 text-xs text-slate-600 max-w-[120px] truncate">{tx.from_entity}</td>
                      <td className="py-2.5 px-4 text-xs text-slate-600 max-w-[120px] truncate">{tx.to_entity}</td>
                      <td className="py-2.5 px-4 text-xs text-slate-600 max-w-[200px] truncate">{tx.description}</td>
                      <td className="py-2.5 px-4 text-xs text-slate-400">{tx.reference}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function FinancialTab({ recipientId, recipient, stats, onRefresh }) {
  const [txs, setTxs] = useState([]);
  const [showAdjust, setShowAdjust] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    api.get(`/recipients/${recipientId}/transactions`).then(r => setTxs(r.data)).catch(() => {});
  }, [recipientId]);

  const handleAdjust = () => {
    if (!newBalance && newBalance !== '0') { toast.error('Enter new balance'); return; }
    setConfirmStep(true);
  };

  const confirmAdjust = async () => {
    setSaving(true);
    try {
      await api.post('/import/balance-adjustment', {
        recipient_id: recipientId, new_balance: parseFloat(newBalance), reason
      });
      toast.success('Balance updated');
      setShowAdjust(false); setConfirmStep(false); setNewBalance(''); setReason('');
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const oldBal = recipient?.wallet_balance || 0;
  const change = (parseFloat(newBalance) || 0) - oldBal;

  const cards = [
    { label: 'Total Allocated', val: recipient?.total_allocated, color: '#0F6CBD' },
    { label: 'Total Spent', val: recipient?.total_spent, color: '#D83B01' },
    { label: 'Available Balance', val: recipient?.wallet_balance, color: '#107C41' },
    { label: 'Financial Requirement', val: recipient?.financial_requirement, color: '#5C2D91' },
    { label: 'Remaining Need', val: Math.max(0, (recipient?.financial_requirement || 0) - (recipient?.total_allocated || 0)), color: '#FFB900' },
  ];

  return (
    <div className="space-y-4" data-testid="financial-tab">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700 font-['Chivo']">Financial Balance</span>
        <Button size="sm" onClick={() => setShowAdjust(true)} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm text-xs" data-testid="adjust-balance-btn">
          <PencilSimple size={14} className="mr-1" /> Adjust Balance
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-sm p-3">
            <span className="text-[10px] font-semibold tracking-[0.06em] uppercase text-slate-400">{c.label}</span>
            <p className="currency text-xl font-semibold mt-1" style={{ color: c.color }}>{fmtCurrency(c.val)}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="p-3 border-b border-slate-200"><span className="text-sm font-semibold text-slate-700 font-['Chivo']">Transaction History ({txs.length})</span></div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0"><tr className="bg-slate-50 border-b border-slate-200">
              {['Date', 'Type', 'Amount', 'From', 'To', 'Reference'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-2 px-4">{h}</th>)}
            </tr></thead>
            <tbody>
              {txs.length === 0 ? <tr><td colSpan={6} className="py-4 text-center text-sm text-slate-400">No transactions</td></tr> :
                txs.map(tx => (
                  <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-4 text-xs text-slate-600">{tx.created_at?.substring(0, 10)}</td>
                    <td className="py-2 px-4"><Badge variant="outline" className="text-xs capitalize">{tx.type?.replace(/_/g, ' ')}</Badge></td>
                    <td className={`py-2 px-4 currency text-sm font-medium ${tx.to_id === recipientId ? 'text-emerald-600' : 'text-red-600'}`}>
                      {tx.to_id === recipientId ? '+' : '-'}{fmtCurrency(tx.amount)}
                    </td>
                    <td className="py-2 px-4 text-xs text-slate-600">{tx.from_entity}</td>
                    <td className="py-2 px-4 text-xs text-slate-600">{tx.to_entity}</td>
                    <td className="py-2 px-4 text-xs text-slate-500">{tx.reference}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Balance Adjustment Dialog */}
      {showAdjust && (
        <Dialog open onOpenChange={() => { setShowAdjust(false); setConfirmStep(false); }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="adjust-balance-dialog">
            <DialogHeader><DialogTitle className="font-['Chivo']">Adjust Balance - {recipient?.name}</DialogTitle></DialogHeader>
            {!confirmStep ? (
              <div className="space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                  <span className="data-label">Current Balance</span>
                  <p className="currency text-xl font-semibold text-slate-900">{fmtCurrency(oldBal)}</p>
                </div>
                <div><Label className="data-label">New Balance ($) *</Label>
                  <Input type="number" step="0.01" value={newBalance} onChange={e => setNewBalance(e.target.value)} className="rounded-sm" data-testid="new-balance-input" placeholder="0.00" /></div>
                <div><Label className="data-label">Reason for adjustment</Label>
                  <Textarea value={reason} onChange={e => setReason(e.target.value)} className="rounded-sm" rows={2} placeholder="e.g. Correction, manual top-up, etc." data-testid="adjust-reason-input" /></div>
                <Button onClick={handleAdjust} className="w-full bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm" data-testid="adjust-preview-btn">Preview Change</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                  <div className="flex items-start gap-2">
                    <Warning size={20} className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm">Confirm Balance Adjustment</p>
                      <div className="mt-3 space-y-2">
                        <div className="flex justify-between"><span className="text-sm text-amber-700">Old Balance:</span><span className="currency font-semibold text-slate-800">{fmtCurrency(oldBal)}</span></div>
                        <div className="flex justify-between"><span className="text-sm text-amber-700">New Balance:</span><span className="currency font-semibold text-slate-800">{fmtCurrency(parseFloat(newBalance))}</span></div>
                        <div className="flex justify-between border-t border-amber-200 pt-2">
                          <span className="text-sm font-medium text-amber-800">Change:</span>
                          <span className={`currency font-bold ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{change >= 0 ? '+' : ''}{fmtCurrency(change)}</span>
                        </div>
                      </div>
                      {reason && <p className="text-xs text-amber-600 mt-2">Reason: {reason}</p>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setConfirmStep(false)} variant="outline" className="flex-1 rounded-sm">Back</Button>
                  <Button onClick={confirmAdjust} disabled={saving} className="flex-1 bg-[#D83B01] hover:bg-[#A52C00] text-white rounded-sm" data-testid="confirm-adjust-btn">
                    {saving ? 'Saving...' : 'Confirm Adjustment'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function FundingRequestsTab({ recipientId, recipientName, onRefresh }) {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: '', description: '', category: '' });
  const [saving, setSaving] = useState(false);

  const fetchReqs = useCallback(async () => {
    try { const { data } = await api.get(`/funding-requests?recipient_id=${recipientId}`); setRequests(data); } catch {}
  }, [recipientId]);
  useEffect(() => { fetchReqs(); }, [fetchReqs]);

  const handleCreate = async () => {
    if (!form.amount || !form.description) { toast.error('Amount and description required'); return; }
    setSaving(true);
    try {
      await api.post('/funding-requests', { recipient_id: recipientId, amount: parseFloat(form.amount), description: form.description, category: form.category });
      toast.success('Request created'); setShowForm(false); setForm({ amount: '', description: '', category: '' }); fetchReqs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') { await api.put(`/funding-requests/${id}/approve`); toast.success('Approved'); onRefresh(); }
      else { await api.put(`/funding-requests/${id}/deny`, { reason: 'Denied by admin' }); toast.success('Denied'); }
      fetchReqs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="funding-requests-tab">
      <div className="flex justify-between items-center">
        <span className="text-sm font-semibold text-slate-700 font-['Chivo']">Funding Requests ({requests.length})</span>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-xs" data-testid="create-funding-request-btn">
          <Plus size={14} className="mr-1" /> New Request
        </Button>
      </div>
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="data-label">Amount *</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="rounded-sm" data-testid="fr-amount-input" /></div>
            <div><Label className="data-label">Category</Label><Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="rounded-sm" /></div>
          </div>
          <div><Label className="data-label">Description *</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-sm" rows={2} data-testid="fr-description-input" /></div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={saving} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-xs" data-testid="fr-submit-btn">{saving ? 'Saving...' : 'Submit'}</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full"><thead><tr className="bg-slate-50 border-b border-slate-200">
          {['Date', 'Amount', 'Description', 'Status', 'Actions'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-2 px-4">{h}</th>)}
        </tr></thead>
        <tbody>
          {requests.length === 0 ? <tr><td colSpan={5} className="py-4 text-center text-sm text-slate-400">No requests</td></tr> :
            requests.map(r => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-2 px-4 text-xs text-slate-600">{r.created_at?.substring(0, 10)}</td>
                <td className="py-2 px-4 currency text-sm">{fmtCurrency(r.amount)}</td>
                <td className="py-2 px-4 text-sm text-slate-700">{r.description}</td>
                <td className="py-2 px-4"><Badge className={`capitalize text-xs status-${r.status}`}>{r.status}</Badge></td>
                <td className="py-2 px-4">
                  {r.status === 'pending' && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-green-600" onClick={() => handleAction(r.id, 'approve')}><Check size={14} /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600" onClick={() => handleAction(r.id, 'deny')}><X size={14} /></Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );
}

function DonorsTab({ stats }) {
  const contributions = stats?.donor_contributions || [];
  return (
    <div className="space-y-4" data-testid="donors-tab">
      <span className="text-sm font-semibold text-slate-700 font-['Chivo']">Contributing Donors ({contributions.length})</span>
      {contributions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={contributions.map(c => ({ name: c.donor_name, value: c.total }))}
                  dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}
                  label={({ name, value }) => `${name.substring(0, 20)}: $${value.toLocaleString()}`}>
                  {contributions.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <RTooltip formatter={v => fmtCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full"><thead><tr className="bg-slate-50 border-b border-slate-200">
          {['Donor', 'Total Contributed', 'Donations'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>)}
        </tr></thead>
        <tbody>
          {contributions.length === 0 ? <tr><td colSpan={3} className="py-4 text-center text-sm text-slate-400">No donor contributions found</td></tr> :
            contributions.map((c, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-3 px-4 text-sm font-medium text-slate-800">{c.donor_name}</td>
                <td className="py-3 px-4 currency text-sm font-medium text-[#0F6CBD]">{fmtCurrency(c.total)}</td>
                <td className="py-3 px-4 text-sm text-slate-600">{c.count}</td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );
}

function DocumentsTab({ recipientId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fetchDocs = useCallback(async () => {
    try { const { data } = await api.get(`/documents/${recipientId}`); setDocs(data); } catch {}
  }, [recipientId]);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('recipient_id', recipientId);
      await api.post(`/documents/upload?recipient_id=${recipientId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded'); fetchDocs();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    setUploading(false); e.target.value = '';
  };
  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/download/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url; a.download = doc.original_filename; a.click();
    } catch { toast.error('Download failed'); }
  };

  return (
    <div className="space-y-4" data-testid="documents-tab">
      <div className="flex justify-between items-center">
        <span className="text-sm font-semibold text-slate-700 font-['Chivo']">Documents ({docs.length})</span>
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} data-testid="upload-doc-input" />
          <Button asChild size="sm" className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-xs" data-testid="upload-doc-btn">
            <span><Upload size={14} className="mr-1" /> {uploading ? 'Uploading...' : 'Upload'}</span>
          </Button>
        </label>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full"><thead><tr className="bg-slate-50 border-b border-slate-200">
          {['Filename', 'Size', 'Uploaded By', 'Date', 'Actions'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-2 px-4">{h}</th>)}
        </tr></thead>
        <tbody>
          {docs.length === 0 ? <tr><td colSpan={5} className="py-4 text-center text-sm text-slate-400">No documents</td></tr> :
            docs.map(d => (
              <tr key={d.id} className="border-b border-slate-100">
                <td className="py-2 px-4 text-sm text-slate-700">{d.original_filename}</td>
                <td className="py-2 px-4 text-xs text-slate-500">{d.size ? `${(d.size / 1024).toFixed(1)} KB` : '-'}</td>
                <td className="py-2 px-4 text-xs text-slate-600">{d.uploaded_by_name}</td>
                <td className="py-2 px-4 text-xs text-slate-500">{d.created_at?.substring(0, 10)}</td>
                <td className="py-2 px-4 flex gap-1">
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-blue-600" onClick={() => handleDownload(d)}><Download size={14} /></Button>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={async () => { await api.delete(`/documents/${d.id}`); toast.success('Deleted'); fetchDocs(); }}><Trash size={14} /></Button>
                </td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );
}
